import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Stars, Badge } from "@/lib/ui";
import { Briefcase } from "lucide-react";
import SlotPicker from "@/components/SlotPicker";
import { useAuth } from "@/context/AuthContext";
import LocationFilter, { DistanceTag } from "@/components/LocationFilter";
import TrustBadges from "@/components/TrustBadges";
import Avatar from "@/components/Avatar";

export default function Helpers() {
  const { user } = useAuth();
  const [helpers, setHelpers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geo, setGeo] = useState({});
  const [area, setArea] = useState("");
  const [slot, setSlot] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = { ...geo, area: area || undefined, tz_offset: new Date().getTimezoneOffset(), at: slot || undefined };
    const t = setTimeout(() => {
      api.get("/helpers", { params }).then((r) => setHelpers(r.data)).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [geo, area, slot]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
      <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">Our caring helpers</h1>
      <p className="text-lg text-slate-600 mt-1 mb-6">Trusted people ready to lend a hand — many verified by our team.</p>
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <LocationFilter geo={geo} setGeo={setGeo} area={area} setArea={setArea} savedCoords={user} />
        <SlotPicker value={slot} onChange={setSlot} />
      </div>

      {loading ? (
        <p className="text-lg text-slate-500">Loading helpers…</p>
      ) : helpers.length === 0 ? (
        <div className="bg-white border border-[#E2D9CE] rounded-2xl p-10 text-center">
          <p className="text-xl font-semibold text-slate-800">{geo.lat != null || area || slot ? "No helpers found for these filters." : "No helpers yet."}</p>
          <p className="text-slate-600 mt-1">{geo.lat != null ? "Try a wider distance." : <>Be the first to <Link to="/register" className="text-[#D96B43] font-semibold">join as a helper</Link>.</>}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {helpers.map((h) => (
            <Link to={`/helpers/${h.id}`} key={h.id} data-testid={`helper-card-${h.id}`}
              className={`bg-white border-2 rounded-2xl p-6 shadow-sm transition-all ${h.is_available ? "border-[#E8F4F1] hover:border-[#2E7D6B]" : "border-slate-200 opacity-60 grayscale hover:opacity-90"}`}>
              <div className="flex items-center gap-4">
                <Avatar user={h} size="md" />
                <div>
                  <p className="text-xl font-semibold text-slate-900">{h.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Stars rating={h.avg_rating} />
                    <span className="text-slate-500 text-sm">({h.review_count})</span>
                  </div>
                  <DistanceTag km={h.distance_km} />
                </div>
              </div>
              <p className="text-slate-600 mt-4 line-clamp-2">{h.bio || "A kind helper on Kindred."}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {h.is_available ? <Badge color="green" testid="available-tag">Available</Badge> : <Badge color="slate" testid="unavailable-tag">Unavailable</Badge>}
                <TrustBadges user={h} />
                <Badge color="gold"><Briefcase className="w-4 h-4" /> {h.completed_jobs} jobs</Badge>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
